import type {
  LinkKind,
  NodeKind,
  Point,
  SwmmLink,
  SwmmNode,
  SwmmProject,
  SwmmRaingage,
  SwmmSubcatchment,
} from "./types.ts";
import { readFlowUnits } from "../units.ts";
import { registerText } from "../../i18n/textRegistry.ts";

const SECTION_HEADER = /^\[(\w+)\]$/;

/** Must not strip a `;` that falls inside a quoted span. `comment` is the trailing text after that `;` (untrimmed leading space kept out), or `null` for a line with no comment at all — distinct from a fully-commented line, whose `code` half comes back empty. */
function stripComment(line: string): { code: string; comment: string | null } {
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '"') inQuotes = !inQuotes;
    else if (line[i] === ";" && !inQuotes) {
      return { code: line.slice(0, i), comment: line.slice(i + 1) };
    }
  }
  return { code: line, comment: null };
}

function tokenize(line: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  while (i < line.length) {
    while (i < line.length && /\s/.test(line[i])) i++;
    if (i >= line.length) break;
    if (line[i] === '"') {
      const close = line.indexOf('"', i + 1);
      const end = close === -1 ? line.length : close;
      tokens.push(line.slice(i + 1, end));
      i = end + 1;
    } else {
      let j = i;
      while (j < line.length && !/\s/.test(line[j])) j++;
      tokens.push(line.slice(i, j));
      i = j;
    }
  }
  return tokens;
}

/**
 * [TITLE] is the one exception — each line is kept as a single-element row,
 * never tokenized, since a project title can contain anything.
 *
 * A section header repeating LATER in the file (real SWMM's own parser
 * supports this — some tools emit a fresh `[XSECTIONS]` block per
 * structure-type group instead of one combined block) must APPEND to that
 * section's existing rows, not start a fresh empty one — overwriting would
 * silently drop everything the first occurrence had already collected.
 */
export function parseInpSections(text: string): Map<string, string[][]> {
  const sections = new Map<string, string[][]>();
  let current: string[][] | undefined;
  let currentName = "";
  for (const rawLine of text.split(/\r?\n/)) {
    const line = stripComment(rawLine).code.trim();
    if (!line) continue;
    const header = line.match(SECTION_HEADER);
    if (header) {
      currentName = header[1].toUpperCase();
      current = sections.get(currentName);
      if (!current) {
        current = [];
        sections.set(currentName, current);
      }
      continue;
    }
    if (!current) continue; // content before the first section header — not a valid .inp, ignore
    current.push(currentName === "TITLE" ? [line] : tokenize(line));
  }
  return sections;
}

/**
 * A row's trailing `;comment` text (e.g. `J1 100 4 0 0 0  ;description`),
 * keyed by `section\0name\0occurrence` (`occurrence` = how many earlier
 * rows in this same section already share that same first-column name —
 * 0 for every ordinary single-row-per-element section, >0 only for a
 * multi-row-per-name section like `[CURVES]`/`[TIMESERIES]`). A fully-
 * commented line (`;...` alone) is NOT captured here — same as
 * `parseInpSections`, it's dropped entirely, on purpose (see
 * `getElementCommentKey` for the read side).
 *
 * A completely separate walk of the same text rather than folded into
 * `parseInpSections` itself, since changing that function's own return
 * shape would ripple through its ~40 existing call sites for a feature
 * most of them never need — this is called only where a comment is
 * actually about to matter (opening a project, exporting one).
 */
export function parseInpComments(text: string): Map<string, string> {
  const comments = new Map<string, string>();
  const occurrenceBySection = new Map<string, Map<string, number>>();
  let currentName = "";
  let currentOccurrences: Map<string, number> | undefined;
  for (const rawLine of text.split(/\r?\n/)) {
    const { code, comment } = stripComment(rawLine);
    const line = code.trim();
    if (!line) continue;
    const header = line.match(SECTION_HEADER);
    if (header) {
      currentName = header[1].toUpperCase();
      currentOccurrences = occurrenceBySection.get(currentName);
      if (!currentOccurrences) {
        currentOccurrences = new Map();
        occurrenceBySection.set(currentName, currentOccurrences);
      }
      continue;
    }
    if (!currentOccurrences) continue;
    const name = currentName === "TITLE" ? "" : (tokenize(line)[0] ?? "");
    const occurrence = currentOccurrences.get(name) ?? 0;
    currentOccurrences.set(name, occurrence + 1);
    if (comment !== null) {
      comments.set(`${currentName}\0${name}\0${occurrence}`, comment);
    }
  }
  return comments;
}

/** The `parseInpComments` key for a section's Nth row sharing `name` — `occurrence` defaults to 0 (every ordinary single-row-per-element section). Shared by both the parse side above and any UI lookup, so the two can never drift apart on key format. */
export function getElementCommentKey(section: string, name: string, occurrence = 0): string {
  return `${section}\0${name}\0${occurrence}`;
}

const NODE_SECTIONS: Record<string, NodeKind> = {
  JUNCTIONS: "junction",
  OUTFALLS: "outfall",
  STORAGE: "storage",
  DIVIDERS: "divider",
};

const LINK_SECTIONS: Record<string, LinkKind> = {
  CONDUITS: "conduit",
  PUMPS: "pump",
  ORIFICES: "orifice",
  WEIRS: "weir",
  OUTLETS: "outlet",
};

/**
 * `deriveProject` already treats a link row missing its own from/to node as
 * if it doesn't exist (no `SwmmLink` to build from one) — every part of the
 * UI (element tree, map, property panel) is built off `SwmmProject`, so such
 * a row is completely invisible there. Left in `rawSections` untouched, it
 * would still ride along on every export, unexplained, since `serializeInp`
 * writes `rawSections` directly, not the derived view. Called once, when a
 * `.inp` file/sample is first opened (`useProjectLifecycle.ts`'s own
 * `openNewFile`) — not on every `deriveProject` call, which would mean
 * re-deciding this on every render instead of once, up front.
 */
export function dropMalformedLinkRows(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Map<string, string[][]> {
  const next = new Map(raw);
  for (const section of Object.keys(LINK_SECTIONS)) {
    const rows = raw.get(section);
    if (!rows || rows.length === 0) continue;
    const kept: string[][] = [];
    for (const row of rows) {
      const [name, fromNode, toNode] = row;
      if (!name || !fromNode || !toNode) {
        warnings?.add(
          registerText(
            "modelWarning.malformedLinkRow",
            `[${section}] row "${name ?? "?"}" is missing its own from/to node.`,
            { section, name: name ?? "?" }
          )
        );
        continue;
      }
      kept.push(row);
    }
    next.set(section, kept);
  }
  return next;
}

function readPoints(rows: string[][]): Map<string, Point[]> {
  const byName = new Map<string, Point[]>();
  for (const row of rows) {
    const [name, xStr, yStr] = row;
    const x = Number(xStr);
    const y = Number(yStr);
    if (!name || !Number.isFinite(x) || !Number.isFinite(y)) continue;
    const list = byName.get(name);
    if (list) list.push({ x, y });
    else byName.set(name, [{ x, y }]);
  }
  return byName;
}

function firstPoints(rows: string[][]): Map<string, Point> {
  const byName = new Map<string, Point>();
  for (const row of rows) {
    const [name, xStr, yStr] = row;
    const x = Number(xStr);
    const y = Number(yStr);
    if (!name || !Number.isFinite(x) || !Number.isFinite(y) || byName.has(name)) continue;
    byName.set(name, { x, y });
  }
  return byName;
}

/** Called again after every edit, on a cloned-and-modified raw map, so the typed views can never drift out of sync with what a re-exported .inp would actually say. */
export function deriveProject(raw: ReadonlyMap<string, string[][]>): SwmmProject {
  const nodes: SwmmNode[] = [];
  for (const [section, kind] of Object.entries(NODE_SECTIONS)) {
    for (const row of raw.get(section) ?? []) {
      if (row[0]) nodes.push({ name: row[0], kind });
    }
  }

  const links: SwmmLink[] = [];
  for (const [section, kind] of Object.entries(LINK_SECTIONS)) {
    for (const row of raw.get(section) ?? []) {
      const [name, fromNode, toNode] = row;
      if (name && fromNode && toNode) links.push({ name, kind, fromNode, toNode });
    }
  }

  const subcatchments: SwmmSubcatchment[] = (raw.get("SUBCATCHMENTS") ?? [])
    .filter((row) => row[0])
    .map((row) => ({ name: row[0], raingage: row[1] ?? "", outlet: row[2] ?? "" }));

  const raingages: SwmmRaingage[] = (raw.get("RAINGAGES") ?? [])
    .filter((row) => row[0])
    .map((row) => ({ name: row[0] }));

  const title = (raw.get("TITLE") ?? []).map((row) => row[0]).join("\n");
  // Normalized (uppercased, validated against the known token set,
  // defaulting to CFS for anything unrecognized) the same way the engine's
  // own `readFlowUnits` reads it — the raw, un-normalized token (e.g. a
  // lowercase "cfs") used to flow straight into the UI instead, where
  // `unitSystemOf`'s exact-case Set lookup would silently misread a valid
  // US-unit project as SI.
  const flowUnits = readFlowUnits(raw);

  return {
    title,
    flowUnits,
    nodes,
    links,
    subcatchments,
    raingages,
    coordinates: firstPoints(raw.get("COORDINATES") ?? []),
    vertices: readPoints(raw.get("VERTICES") ?? []),
    polygons: readPoints(raw.get("POLYGONS") ?? []),
    symbols: firstPoints(raw.get("SYMBOLS") ?? []),
    rawSections: raw,
  };
}

export function parseInp(text: string): SwmmProject {
  return deriveProject(parseInpSections(text));
}
